/**
 * ApprovalLetterViewerEmbed — shared regulatory-document viewer bound
 * to the FDA approval-letter API family.
 */
import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { ChunkMetadata } from '../../types/viewer';
import { approvalLetterApi } from '../../api/createViewerApi';
import RegulatoryViewerEmbed, {
  type ChunkAnchor,
  type RegulatoryViewerHandle,
} from './RegulatoryViewerEmbed';

export type ApprovalLetterViewerHandle = RegulatoryViewerHandle;

interface ApprovalLetterViewerEmbedProps {
  docId: number;
  highlightChunkIds?: number[];
  chunkAnchors?: Record<number, ChunkAnchor[]>;
  /** page_num -> anchor for a chunk-free (progressive-read) citation.
   *  Forwarded verbatim to RegulatoryViewerEmbed via the props spread. */
  pageAnchors?: Record<number, ChunkAnchor[]> | undefined;
  highlightTerm?: string | null;
  highlightTerms?: string[] | null;
  englishExact?: boolean;
  highlightColors?: (string | undefined)[] | null;
  landingPage?: number | undefined;
  onChunkClick?: (chunk: ChunkMetadata) => void;
}

const ApprovalLetterViewerEmbed = forwardRef<
  ApprovalLetterViewerHandle,
  ApprovalLetterViewerEmbedProps
>(function ApprovalLetterViewerEmbed(props, ref) {
  const innerRef = useRef<RegulatoryViewerHandle>(null);
  useImperativeHandle(ref, () => ({
    scrollToPage: (n) => innerRef.current?.scrollToPage(n),
    highlightChunks: (ids) => innerRef.current?.highlightChunks(ids),
    scrollToSection: (s) => innerRef.current?.scrollToSection(s) ?? false,
  }));
  return (
    <RegulatoryViewerEmbed
      ref={innerRef}
      viewerApi={approvalLetterApi}
      {...props}
    />
  );
});

export default ApprovalLetterViewerEmbed;
